/**
 * 知识卡片 — 驼峰中的统一卡片展示
 *
 * 三种类型各有不同的展示方式，点击跳转详情页。
 */

import { useNavigate } from 'react-router';
import { BookOpen, FileText, StickyNote, Trash2, ArrowRight } from 'lucide-react';
import { Card, Badge, IconButton } from '@hump/ui';
import type { KnowledgeItem } from '@hump/shared-types';
import { useKnowledgeStore } from '../stores/knowledge';
import { useConfirmStore } from './ConfirmDialog';

const typeConfig = {
  word: { icon: BookOpen, label: '生词', color: 'text-oasis' },
  snippet: { icon: FileText, label: '段落', color: 'text-camel' },
  note: { icon: StickyNote, label: '批注', color: 'text-info' },
} as const;

export function KnowledgeCard({ item }: { item: KnowledgeItem }) {
  const navigate = useNavigate();
  const remove = useKnowledgeStore((s) => s.remove);
  const config = typeConfig[item.type];
  const Icon = config.icon;

  const confirmDialog = useConfirmStore((s) => s.confirm);

  async function handleDelete(e: React.MouseEvent) {
    e.stopPropagation();
    const ok = await confirmDialog({
      title: '删除知识点',
      message: '确定删除这个知识点？',
      confirmText: '删除',
      danger: true,
    });
    if (ok) remove(item.id);
  }

  return (
    <Card onClick={() => navigate(`/hump/${item.type}/${item.id}`)}>
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 ${config.color} shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          {/* 类型标签 */}
          <Badge variant="default" className="mb-1">{config.label}</Badge>

          {/* 生词 */}
          {item.type === 'word' && (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-medium text-charcoal dark:text-charcoal-light">{item.word}</h3>
                {item.phonetic && <span className="text-sm text-charcoal/50 dark:text-charcoal-light/50">{item.phonetic}</span>}
                {item.pos && <Badge variant="info">{item.pos}</Badge>}
              </div>
              {item.definitions && item.definitions.length > 0 && (
                <p className="text-sm text-charcoal/70 dark:text-charcoal-light/70 mt-1">{item.definitions.join('；')}</p>
              )}
            </>
          )}

          {/* 段落收藏 */}
          {item.type === 'snippet' && (
            <p className="text-sm text-charcoal/80 dark:text-charcoal-light/80 line-clamp-3">{item.text}</p>
          )}

          {/* 批注 */}
          {item.type === 'note' && (
            <>
              <p className="text-sm text-charcoal/80 dark:text-charcoal-light/80 line-clamp-2">{item.note}</p>
              {item.context && (
                <p className="text-xs text-charcoal/40 dark:text-charcoal-light/40 mt-1 italic line-clamp-1">原文：{item.context}</p>
              )}
            </>
          )}

          {/* 来源 */}
          {item.docTitle && (
            <p className="text-xs text-charcoal/30 dark:text-charcoal-light/30 mt-2 flex items-center gap-1">
              <FileText className="w-3 h-3" />
              {item.docTitle}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <IconButton size="sm" onClick={handleDelete} aria-label="删除">
            <Trash2 className="w-4 h-4" />
          </IconButton>
          <ArrowRight className="w-4 h-4 text-charcoal/20 dark:text-charcoal-light/20" />
        </div>
      </div>
    </Card>
  );
}
